import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import { cn } from '@/lib/cn';

type Props = {
  eyebrow?: string;
  headline: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  illustration?: ReactNode;
  align?: 'split' | 'centered';
  className?: string;
};

const container = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};
const child = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export function Hero({ eyebrow, headline, description, actions, illustration, align = 'split', className }: Props) {
  return (
    <section
      className={cn(
        'relative min-h-dvh lg:h-dvh flex items-center px-6 md:px-12 lg:px-20 pt-24 pb-12 md:pt-28 md:pb-16',
        className,
      )}
    >
      <motion.div
        className={cn(
          'mx-auto max-w-[1440px] w-full',
          align === 'split'
            ? 'grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center'
            : 'flex flex-col items-center text-center max-w-4xl',
        )}
        variants={container}
        initial="hidden"
        animate="visible"
      >
        <div className={cn('flex flex-col gap-8', align === 'centered' && 'items-center')}>
          {eyebrow && <motion.span className="eyebrow" variants={child}>{eyebrow}</motion.span>}
          <motion.h1 className="display-xl" variants={child}>{headline}</motion.h1>
          {description && (
            <motion.p className="text-lg md:text-xl text-ink-secondary max-w-xl leading-relaxed" variants={child}>
              {description}
            </motion.p>
          )}
          {actions && (
            <motion.div className="flex flex-wrap gap-4 mt-2" variants={child}>
              {actions}
            </motion.div>
          )}
        </div>
        {illustration && align === 'split' && (
          <motion.div className="text-ink-primary w-full max-w-md lg:max-w-none lg:justify-self-end" variants={child}>
            {illustration}
          </motion.div>
        )}
      </motion.div>
    </section>
  );
}
